"use client";

import React, { useState, useEffect } from "react";
import { useCartStore } from "../store/cartStore";
import Link from "next/link";
import { useRouter } from "next/navigation";

export default function CartPage() {
  const router = useRouter();
  const { items, removeFromCart, updateQuantity } = useCartStore();

  // Локальні стани для UX з АТД
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [itemSettings, setItemSettings] = useState<Record<string, any>>({});
  
  // Дані для швидкої реєстрації / оформлення
  const [clientName, setClientName] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [globalComment, setGlobalComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Ініціалізація налаштувань для товарів (Нова Пошта, Коментарі)
  useEffect(() => {
    const newSettings = { ...itemSettings };
    let changed = false;
    items.forEach((item) => {
      if (!newSettings[item.article]) {
        newSettings[item.article] = {
          suppComment: "",
          stickerComment: "",
          deliveryMethod: "pickup",
          city: "",
          branch: "",
          recipient: "",
          phone: "",
        };
        changed = true;
      }
      // Автоматично виділяємо нові товари
      if (!selectedIds.has(item.article)) {
        setSelectedIds((prev) => new Set(prev).add(item.article));
      }
    });
    if (changed) setItemSettings(newSettings);
  }, [items]);

  const toggleSelection = (article: string) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(article)) newSet.delete(article);
    else newSet.add(article);
    setSelectedIds(newSet);
  };

  const toggleAll = () => {
    if (selectedIds.size === items.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(items.map((i) => i.article)));
  };

  const updateItemSetting = (article: string, field: string, value: string) => {
    setItemSettings((prev) => ({
      ...prev,
      [article]: { ...prev[article], [field]: value },
    }));
  };

  const applyDeliveryToAll = (sourceArticle: string) => {
    const source = itemSettings[sourceArticle];
    const updated = { ...itemSettings };
    Object.keys(updated).forEach((key) => {
      updated[key] = {
        ...updated[key],
        deliveryMethod: source.deliveryMethod,
        city: source.city,
        branch: source.branch,
        recipient: source.recipient,
        phone: source.phone,
      };
    });
    setItemSettings(updated);
    alert("Дані доставки успішно застосовано до всіх позицій у кошику!");
  };

  const selectedTotalUah = items
    .filter((i) => selectedIds.has(i.article))
    .reduce((sum, i) => sum + (i.price_uah * i.quantity), 0);

  const handleCheckout = async () => {
    if (selectedIds.size === 0) return alert("Оберіть хоча б один товар для замовлення!");
    if (!clientName.trim() || !clientPhone.trim()) {
      return alert("Будь ласка, вкажіть ваше Ім'я та Телефон у блоці швидкої реєстрації!");
    }

    const selectedItems = items.filter((i) => selectedIds.has(i.article));
    const payloadItems = [];

    for (const item of selectedItems) {
      const settings = itemSettings[item.article];
      let deliveryDetails = "";

      if (settings.deliveryMethod === "novaposhta") {
        if (!settings.city || !settings.branch || !settings.recipient || !settings.phone) {
          return alert(`Будь ласка, заповніть всі дані Нової Пошти для деталі: ${item.brand} ${item.article}`);
        }
        deliveryDetails = `м. ${settings.city}, Відділення: ${settings.branch} | Одержувач: ${settings.recipient}, Тел: ${settings.phone}`;
      }

      payloadItems.push({
        sku: item.article,
        brand: item.brand,
        name: item.name,
        price_usd: item.price_usd || 0,
        price_uah: item.price_uah || 0,
        quantity: item.quantity,
        weight: item.weight || 0,
        supplier_code: item.supplier_code || "LOCAL",
        supplier_comment: settings.suppComment,
        sticker_comment: settings.stickerComment,
        delivery_method: settings.deliveryMethod,
        delivery_details: deliveryDetails,
      });
    }

    setIsSubmitting(true);
    try {
      const res = await fetch("https://api.autologistic.com.ua/api/orders/checkout", {
        method: "POST",
        headers: { 
            "Content-Type": "application/json",
            "X-API-Key": "autologistic_bot_token_2026_secure" // Ключ з бэкенду
        },
        body: JSON.stringify({
          items: payloadItems,
          client_name: clientName,
          client_phone: clientPhone,
          global_comment: globalComment,
        }),
      });

      const data = await res.json();
      if (data.status === "success") {
        alert(`✅ Замовлення №${data.order_id} успішно оформлено! Очікуйте дзвінка менеджера.`);
        selectedItems.forEach((i) => removeFromCart(i.id as any));
        setGlobalComment("");
        router.push("/"); 
      } else {
        alert("Помилка при оформленні: " + (data.detail || data.message));
      }
    } catch (e) {
      console.error(e);
      alert("Помилка з'єднання з сервером.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="min-h-[70vh] bg-gray-50 flex flex-col items-center justify-center p-4">
        <div className="bg-white p-12 rounded-2xl shadow-sm border border-gray-100 text-center">
            <svg className="w-24 h-24 mx-auto mb-6 text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"></path></svg>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Ваш кошик порожній</h2>
            <Link href="/" className="bg-gradient-to-br from-[#ff6600] to-[#ff4500] text-white px-8 py-3 rounded-full font-bold shadow-md hover:shadow-lg transition-all">Перейти до каталогу</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-black text-gray-900 mb-8">Оформлення замовлення</h1>

        {/* ТАБЛИЦЯ КОШИКА (Взято з логіки АТД) */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-8">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="p-4 text-center">
                    <input type="checkbox" checked={selectedIds.size === items.length} onChange={toggleAll} className="w-5 h-5 text-[#ff6600] rounded focus:ring-[#ff6600] cursor-pointer" />
                  </th>
                  <th className="p-4 font-bold text-gray-500 uppercase text-xs">Деталь</th>
                  <th className="p-4 font-bold text-gray-500 uppercase text-xs text-center">Постачальник</th>
                  <th className="p-4 font-bold text-gray-500 uppercase text-xs text-center">Кіл-ть</th>
                  <th className="p-4 font-bold text-gray-500 uppercase text-xs text-right">Ціна</th>
                  <th className="p-4 font-bold text-gray-500 uppercase text-xs text-right">Сума</th>
                  <th className="p-4"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((item) => {
                  const setts = itemSettings[item.article] || {};
                  return (
                    <React.Fragment key={item.article}>
                      <tr className="hover:bg-gray-50/50 transition-colors">
                        <td className="p-4 text-center align-top border-r border-gray-50">
                          <input type="checkbox" checked={selectedIds.has(item.article)} onChange={() => toggleSelection(item.article)} className="w-5 h-5 text-[#ff6600] rounded focus:ring-[#ff6600] cursor-pointer" />
                        </td>
                        <td className="p-4 align-top">
                          <div className="font-bold text-[#005a9e]">{item.brand}</div>
                          <div className="font-mono font-bold text-gray-900 text-lg">{item.article}</div>
                          <div className="text-gray-500 text-xs mt-1">{item.name}</div>
                        </td>
                        <td className="p-4 align-top text-center">
                          <div className="font-bold text-gray-700">{item.delivery_days} дн.</div>
                          <div className="text-xs text-gray-500 mt-1">{item.supplier_code || 'Склад AL'}</div>
                        </td>
                        <td className="p-4 align-top text-center">
                          <input type="number" min="1" value={item.quantity} onChange={(e) => updateQuantity(item.id as any, parseInt(e.target.value) || 1)} className="w-20 border border-gray-300 rounded-lg text-center p-2 font-bold focus:border-[#ff6600] outline-none" />
                        </td>
                        <td className="p-4 align-top text-right">
                          <div className="font-bold text-gray-900 text-base">{item.price_uah.toFixed(0)} ₴</div>
                          <div className="text-xs text-gray-400 font-bold mt-1">{(item.price_usd || 0).toFixed(2)} $</div>
                        </td>
                        <td className="p-4 align-top text-right font-black text-gray-900 text-lg">
                          {(item.price_uah * item.quantity).toFixed(0)} ₴
                        </td>
                        <td className="p-4 align-top text-center text-red-400 hover:text-red-600 cursor-pointer" onClick={() => removeFromCart(item.id as any)}>
                          <svg className="w-6 h-6 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
                        </td>
                      </tr>

                      {/* БЛОК НАЛАШТУВАНЬ ТОВАРУ (Стікери, Нова Пошта) */}
                      <tr className="bg-gray-50/50">
                        <td colSpan={7} className="p-4 pl-14">
                          <div className="flex flex-wrap gap-5 text-sm">
                            <div className="flex-1 min-w-[200px]">
                              <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wide mb-1">Коментар постачальнику</label>
                              <input type="text" value={setts.suppComment || ''} onChange={(e) => updateItemSetting(item.article, 'suppComment', e.target.value)} placeholder="Напр: перевірте по VIN..." className="w-full border border-gray-300 rounded-lg p-2.5 text-xs outline-none focus:border-[#ff6600]" />
                            </div>
                            <div className="flex-1 min-w-[200px]">
                              <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wide mb-1">Стікер для клієнта</label>
                              <input type="text" value={setts.stickerComment || ''} onChange={(e) => updateItemSetting(item.article, 'stickerComment', e.target.value)} placeholder="Напр: Замовлення №44 (Іванов)" className="w-full border border-gray-300 rounded-lg p-2.5 text-xs outline-none focus:border-[#ff6600]" />
                            </div>
                            
                            <div className="flex-1 min-w-[300px]">
                              <div className="flex justify-between items-center mb-1">
                                <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wide">Доставка для цієї деталі</label>
                                {items.length > 1 && (
                                  <button onClick={() => applyDeliveryToAll(item.article)} className="text-[10px] text-blue-600 font-bold hover:underline">Застосувати для всіх</button>
                                )}
                              </div>
                              <div className="flex flex-col gap-2">
                                <select value={setts.deliveryMethod || 'pickup'} onChange={(e) => updateItemSetting(item.article, 'deliveryMethod', e.target.value)} className="w-full border border-gray-300 rounded-lg p-2.5 text-xs outline-none focus:border-[#ff6600] bg-white">
                                  <option value="pickup">Самовивіз зі складу в Одесі</option>
                                  <option value="novaposhta">Нова Пошта (По Україні)</option>
                                </select>
                                
                                {setts.deliveryMethod === 'novaposhta' && (
                                  <div className="grid grid-cols-2 gap-2 bg-white p-3 rounded-lg border border-gray-200">
                                    <input type="text" value={setts.city || ''} onChange={(e) => updateItemSetting(item.article, 'city', e.target.value)} placeholder="Місто" className="border border-gray-300 rounded p-2 text-xs outline-none focus:border-[#ff6600]" />
                                    <input type="text" value={setts.branch || ''} onChange={(e) => updateItemSetting(item.article, 'branch', e.target.value)} placeholder="№ Відділення" className="border border-gray-300 rounded p-2 text-xs outline-none focus:border-[#ff6600]" />
                                    <input type="text" value={setts.recipient || ''} onChange={(e) => updateItemSetting(item.article, 'recipient', e.target.value)} placeholder="ПІБ Одержувача" className="border border-gray-300 rounded p-2 text-xs outline-none focus:border-[#ff6600] col-span-2" />
                                    <input type="text" value={setts.phone || ''} onChange={(e) => updateItemSetting(item.article, 'phone', e.target.value)} placeholder="Телефон (063...)" className="border border-gray-300 rounded p-2 text-xs outline-none focus:border-[#ff6600] col-span-2" />
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* БЛОК ШВИДКОЇ РЕЄСТРАЦІЇ / КОНТАКТНИХ ДАНИХ */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8 mb-8">
            <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
              <svg className="w-6 h-6 text-[#ff6600]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path></svg>
              Контактні дані (Швидке оформлення)
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Ваше Ім'я та Прізвище *</label>
                    <input type="text" value={clientName} onChange={(e) => setClientName(e.target.value)} placeholder="Іванов Іван" className="w-full border border-gray-300 rounded-xl p-4 focus:ring-2 focus:ring-[#ff6600] focus:border-[#ff6600] outline-none transition-shadow" />
                </div>
                <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Ваш Телефон *</label>
                    <input type="tel" value={clientPhone} onChange={(e) => setClientPhone(e.target.value)} placeholder="+38 (000) 000-00-00" className="w-full border border-gray-300 rounded-xl p-4 focus:ring-2 focus:ring-[#ff6600] focus:border-[#ff6600] outline-none transition-shadow" />
                </div>
                <div className="md:col-span-2">
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Загальний коментар до замовлення</label>
                    <textarea value={globalComment} onChange={(e) => setGlobalComment(e.target.value)} rows={2} placeholder="Побажання щодо доставки, час для дзвінка тощо..." className="w-full border border-gray-300 rounded-xl p-4 focus:ring-2 focus:ring-[#ff6600] focus:border-[#ff6600] outline-none transition-shadow resize-none"></textarea>
                </div>
            </div>
        </div>

        {/* ФІНАЛЬНИЙ БЛОК ОФОРМЛЕННЯ */}
        <div className="bg-gray-100 rounded-2xl p-6 md:p-8 flex flex-col md:flex-row justify-between items-center gap-6 border border-gray-200">
          <div className="text-center md:text-left">
            <div className="text-sm text-gray-500 font-bold uppercase mb-1">До сплати:</div>
            <div className="text-4xl font-black text-gray-900">{selectedTotalUah.toFixed(0)} ₴</div>
            <div className="text-xs text-gray-400 mt-2">* Вартість доставки розраховується перевізником</div>
          </div>
          <button 
            onClick={handleCheckout} 
            disabled={isSubmitting || selectedTotalUah === 0}
            className="w-full md:w-auto bg-gradient-to-br from-[#ff6600] to-[#ff4500] hover:scale-105 disabled:transform-none disabled:from-gray-400 disabled:to-gray-500 text-white font-bold py-4 px-12 rounded-xl shadow-lg transition-all text-xl flex items-center justify-center gap-3"
          >
            {isSubmitting ? (
              <><svg className="w-6 h-6 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg> Оформлення...</>
            ) : (
              <><svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg> Оформити замовлення</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
